import Image from 'next/image'
import Link from 'next/link'
import {NextSeo} from 'next-seo'
import {fetchShopifyGraphQL} from '@/utils/shopify'
import SeoInformation from '@/components/SeoInformation'

const TagPage = ({articles, tagName}: {articles: any[]; tagName: string}) => {
  return (
    <>
      <SeoInformation
        pageTitle={`${tagName} Articles - Lifestyle Journal`}
        pageDescription={`Browse all articles tagged with ${tagName} on our Lifestyle Journal.`}
        canonicalUrl={`https://www.deliriouseyewear.com/lifestyle-journal/tag/${encodeURIComponent(
          tagName.toLowerCase().replace(/\s+/g, '-'),
        )}`}
      />

      <div className="mx-auto max-w-7xl px-4 py-20">
        <h1 className="my-10 text-center font-austin text-3xl md:text-3xl">
          Articles tagged with: <span className="text-stone-700">"{tagName}"</span>
        </h1>

        {articles.length === 0 ? (
          <p className="text-center text-gray-600">No articles found for this tag.</p>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {articles.map((article) => (
              <Link href={`/lifestyle-journal/${article.handle}`} key={article.id} className="group">
                <article className="overflow-hidden rounded-lg bg-white shadow transition hover:shadow-lg">
                  {article.image?.url && ( // Check if image exists before rendering
                    <Image
                      src={article.image.url}
                      alt={article.image.altText || article.title}
                      width={800}
                      height={500}
                      className="h-64 w-full object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="mb-2 text-xs text-gray-500">
                      {new Date(article.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      })}
                    </div>
                    <h2 className="mb-2 text-lg font-semibold group-hover:underline">{article.title}</h2>
                    <p className="line-clamp-3 text-sm text-gray-700">{article.excerpt}</p>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

async function getArticlesForTagPage(blogHandle: string) {
  const articlesRes = await fetchShopifyGraphQL(`
    {
      blog(handle: "${blogHandle}") {
        articles(first: 50, sortKey: PUBLISHED_AT, reverse: true) { # Fetch enough to get all tags and articles
          nodes {
            id
            title
            handle
            publishedAt
            excerpt
            tags
            image {
              url
              altText
            }
          }
        }
      }
    }
  `)
  return articlesRes.data.blog.articles.nodes
}

export async function getServerSideProps({params}: {params: {tag: string}}) {
  const blogHandle = 'notizie'
  const requestedTagSlug = decodeURIComponent(params.tag)

  const allArticles = await getArticlesForTagPage(blogHandle)

  const filteredArticles = allArticles.filter(
    (article: any) =>
      article.tags?.some(
        (tag: string) => encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-')) === requestedTagSlug,
      ),
  )

  const tagName =
    filteredArticles.length > 0
      ? filteredArticles[0].tags.find(
          (tag: string) => encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-')) === requestedTagSlug,
        )
      : decodeURIComponent(params.tag).replace(/-/g, ' ')

  if (!filteredArticles.length) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      articles: filteredArticles,
      tagName: tagName,
    },
  }
}

export default TagPage
